import { formatiereZahl, OHNE_WERT } from './WertEinheit';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EINHEITENKATALOG (TX-STANDARD S1.7, Paket TX1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eine Zahl, eine Darstellung: Seite, PDF und Excel zeigen denselben Wert mit
 * derselben Einheit und denselben Nachkommastellen. Gerundet wird NUR für die
 * Anzeige.
 *
 * Das C#-Gegenstück steht in
 * `src/Tuv.Berichte.Layout/Gestaltung/Einheitenkatalog.cs`; ein Unit-Test dort
 * liest DIESE Datei und vergleicht beide Kataloge Zeile für Zeile. Deshalb
 * steht jede Größe auf genau einer Zeile in der Form
 *     schluessel: { einheit: '…', nachkomma: n, bezeichnung: '…' },
 *
 * Nach GRÖSSE, nicht nach Einheit: m³/h Luft ohne, m³/h Wasser mit zwei
 * Nachkommastellen. Nennweiten (DN), Querschnitte und Klassen sind
 * Bezeichnungen und stehen nicht hier.
 *
 * Abweichen darf eine Bahn nur mit Begründung im Befund (S1.7); bestätigt
 * wird der Katalog von der Fachplanung (Fachfrage QUE-15).
 */

export interface Groessenangabe {
  einheit: string;
  nachkomma: number;
  bezeichnung: string;
}

export const GROESSEN = {
  leistung_kw: { einheit: 'kW', nachkomma: 1, bezeichnung: 'Leistung (Heiz-, Kühl-, elektrische Leistung)' },
  leistung_w: { einheit: 'W', nachkomma: 0, bezeichnung: 'Leistung je Raum oder Gerät' },
  leistung_flaeche: { einheit: 'W/m²', nachkomma: 1, bezeichnung: 'flächenbezogene Leistung' },
  volumenstrom_luft: { einheit: 'm³/h', nachkomma: 0, bezeichnung: 'Volumenstrom Luft' },
  volumenstrom_wasser: { einheit: 'm³/h', nachkomma: 2, bezeichnung: 'Volumenstrom Wasser' },
  volumenstrom_wasser_ls: { einheit: 'l/s', nachkomma: 2, bezeichnung: 'Volumenstrom Wasser' },
  massenstrom: { einheit: 'kg/h', nachkomma: 0, bezeichnung: 'Massenstrom' },
  druck_pa: { einheit: 'Pa', nachkomma: 0, bezeichnung: 'Druck, Druckverlust' },
  druck_kpa: { einheit: 'kPa', nachkomma: 1, bezeichnung: 'Druck, Druckverlust' },
  druck_bar: { einheit: 'bar', nachkomma: 2, bezeichnung: 'Druck' },
  temperatur: { einheit: '°C', nachkomma: 1, bezeichnung: 'Temperatur' },
  temperaturdifferenz: { einheit: 'K', nachkomma: 1, bezeichnung: 'Temperaturdifferenz' },
  geschwindigkeit: { einheit: 'm/s', nachkomma: 2, bezeichnung: 'Geschwindigkeit' },
  laenge_m: { einheit: 'm', nachkomma: 2, bezeichnung: 'Länge Leitung, Kanal' },
  laenge_mm: { einheit: 'mm', nachkomma: 0, bezeichnung: 'Bauteilmaß' },
  flaeche: { einheit: 'm²', nachkomma: 2, bezeichnung: 'Fläche' },
  volumen: { einheit: 'm³', nachkomma: 1, bezeichnung: 'Volumen' },
  uwert: { einheit: 'W/(m²·K)', nachkomma: 2, bezeichnung: 'Wärmedurchgangskoeffizient' },
  schallpegel_db: { einheit: 'dB', nachkomma: 1, bezeichnung: 'Schallpegel' },
  schallpegel_dba: { einheit: 'dB(A)', nachkomma: 1, bezeichnung: 'Schallpegel A-bewertet' },
  strom: { einheit: 'A', nachkomma: 1, bezeichnung: 'Strom' },
  spannung: { einheit: 'V', nachkomma: 0, bezeichnung: 'Spannung' },
  spannungsfall: { einheit: '%', nachkomma: 2, bezeichnung: 'Spannungsfall' },
  anteil: { einheit: '%', nachkomma: 1, bezeichnung: 'Anteil' },
  energie_kwh: { einheit: 'kWh/a', nachkomma: 0, bezeichnung: 'Energie je Jahr' },
  energie_mwh: { einheit: 'MWh/a', nachkomma: 1, bezeichnung: 'Energie je Jahr' },
  masse: { einheit: 'kg', nachkomma: 1, bezeichnung: 'Masse' },
  inhalt_l: { einheit: 'l', nachkomma: 1, bezeichnung: 'Inhalt' },
} as const satisfies Record<string, Groessenangabe>;

export type Groesse = keyof typeof GROESSEN;

/** Einheit und Nachkommastellen einer Größe. */
export function groesse(g: Groesse): Groessenangabe {
  return GROESSEN[g];
}

/**
 * Die Felder, die eine `DataTable`-Spalte für eine Größe braucht:
 *
 *     { schluessel: 'leistung', titel: 'Leistung', wert: (z) => z.kw,
 *       ...spalteFuer('leistung_kw') }
 *
 * Damit stehen Einheit (im Kopf) und Nachkommastellen nie mehr von Hand in der
 * Seite.
 */
export function spalteFuer(g: Groesse): { art: 'zahl'; einheit: string; nachkomma: number } {
  const { einheit, nachkomma } = GROESSEN[g];
  return { art: 'zahl', einheit, nachkomma };
}

/**
 * Wert mit den Nachkommastellen der Größe, deutsch formatiert, ohne Einheit
 * (die Einheit steht im Kopf). Fehlstelle „—“.
 */
export function formatiereGroesse(wert: number | null | undefined, g: Groesse): string {
  return formatiereZahl(wert, GROESSEN[g].nachkomma);
}

/** Wert und Einheit als Text mit schmalem Leerzeichen, z. B. für Kennzahlen. */
export function mitEinheit(wert: number | null | undefined, g: Groesse): string {
  const zahl = formatiereGroesse(wert, g);
  return zahl === OHNE_WERT ? zahl : `${zahl} ${GROESSEN[g].einheit}`;
}
